import type { MonthLandingLinesResponse } from "@shared-core/data/api-types";
import {
  formatCount,
  formatCurrencyGbp,
  formatCurrencyGbpCompact,
} from "@shared-core/domain/formatters";
import {
  formatSnapshotDay,
  type MonthLandingMetric,
} from "@shared-core/domain/month-landing";
import {
  distinctJobCount,
  layerDateColumn,
  snapshotDrift,
} from "@shared-core/domain/month-landing-lines";

// The jobs behind one band of the Month Landing chart — "what is in Due, not
// approved?" answered with the jobs themselves.
//
// Presentational only: the tab owns the fetch, the CSV and which band is open.
// Follows the drill pattern already on this page (the quote panel and, before
// it, WallboardReconciliationView): tie-out header, capped table, CSV for the
// full population.
//
// The header carries the one thing a reader needs that the numbers cannot say
// themselves: the bar above is this morning's snapshot and these jobs are live,
// so when the two totals differ the panel names the difference instead of
// leaving it to be discovered.

export type MonthLandingLinesState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; lines: MonthLandingLinesResponse };

const ON_SCREEN_LINE_LIMIT = 50;

export interface MonthLandingLayerLinesPanelProps {
  state: MonthLandingLinesState;
  metric: MonthLandingMetric;
  /** The band's own label on the chart — passed in so the two cannot drift. */
  layerLabel: string;
  /** The band's figure as the chart displays it, for the tie-out. */
  bandTotal: number;
  monthLabel: string;
  onRetry: () => void;
  onDownloadCsv: () => void;
  onClose: () => void;
}

export function MonthLandingLayerLinesPanel({
  state,
  metric,
  layerLabel,
  bandTotal,
  monthLabel,
  onRetry,
  onDownloadCsv,
  onClose,
}: MonthLandingLayerLinesPanelProps) {
  if (state.status === "loading") {
    return (
      <div className="border-border bg-surface text-muted-foreground rounded-lg border p-8 text-center text-sm">
        Loading the jobs in {layerLabel.toLowerCase()}…
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="border-border bg-surface flex flex-col items-center justify-center gap-2 rounded-lg border p-8 text-center">
        <p className="text-destructive text-sm">
          Couldn’t load the jobs in {layerLabel.toLowerCase()}.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="text-primary text-sm underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  const { lines } = state;
  const total =
    metric === "contribution" ? lines.total_contribution : lines.total_revenue;
  const drift = snapshotDrift(bandTotal, total);
  const jobCount = distinctJobCount(lines.lines);
  const dateColumn = layerDateColumn(lines.layer);
  const shown = lines.lines.slice(0, ON_SCREEN_LINE_LIMIT);
  const hasMoreThanShown = lines.lines.length > shown.length;

  return (
    <div className="border-border bg-surface overflow-hidden rounded-lg border">
      <div className="border-border bg-accent flex flex-wrap items-start justify-between gap-3 border-b px-4 py-3">
        <div className="space-y-1">
          <h3 className="text-foreground text-sm font-semibold">
            {layerLabel} — {formatCurrencyGbpCompact(bandTotal)}
          </h3>
          <p className="text-muted-foreground text-xs">
            {formatCount(jobCount)} job{jobCount === 1 ? "" : "s"} in{" "}
            {monthLabel}, totalling{" "}
            <span className="text-foreground font-medium">
              {formatCurrencyGbpCompact(total)}
            </span>
            .
          </p>
          {drift !== null ? (
            <p className="text-xs text-amber-800">
              {formatCurrencyGbpCompact(Math.abs(drift))}{" "}
              {drift > 0
                ? "more on the bar than in this table"
                : "more here than on the bar"}{" "}
              — the bar is this morning’s snapshot and these jobs are live, so
              work {drift > 0 ? "has moved on since" : "has been added since"}.
            </p>
          ) : (
            <p className="text-muted-foreground text-xs">
              Live as of {formatSnapshotDay(lines.as_of)}, and currently
              matching the bar.
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-muted-foreground text-xs tabular-nums">
            {formatCount(lines.lines.length)} row
            {lines.lines.length === 1 ? "" : "s"}
            {hasMoreThanShown ? ` · showing first ${shown.length}` : ""}
          </span>
          <button
            type="button"
            onClick={onDownloadCsv}
            disabled={lines.lines.length === 0}
            className="border-border text-foreground hover:bg-muted/60 inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-60"
          >
            Download CSV
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label={`Close ${layerLabel}`}
            className="border-border text-muted-foreground hover:bg-muted/60 hover:text-foreground inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {lines.lines.length === 0 ? (
        <div className="text-muted-foreground p-8 text-center text-sm">
          No jobs in {layerLabel.toLowerCase()} for {monthLabel}.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground border-border border-b">
                <th className="px-3 py-2.5 text-left font-semibold tracking-wide uppercase whitespace-nowrap">
                  Job
                </th>
                <th className="px-3 py-2.5 text-left font-semibold tracking-wide uppercase">
                  Customer
                </th>
                <th className="px-3 py-2.5 text-left font-semibold tracking-wide uppercase whitespace-nowrap">
                  Status
                </th>
                {dateColumn && (
                  <th className="px-3 py-2.5 text-left font-semibold tracking-wide uppercase whitespace-nowrap">
                    {dateColumn.heading}
                  </th>
                )}
                <th className="px-3 py-2.5 text-left font-semibold tracking-wide uppercase whitespace-nowrap">
                  Lands on
                </th>
                <th className="px-3 py-2.5 text-right font-semibold tracking-wide uppercase whitespace-nowrap">
                  Value
                </th>
                <th className="px-3 py-2.5 text-right font-semibold tracking-wide uppercase whitespace-nowrap">
                  Contribution
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((line, i) => (
                <tr
                  key={`${line.job_no || "no-job"}-${line.due_day}-${i}`}
                  className="border-border/60 hover:bg-muted/40 border-b last:border-0"
                >
                  <td className="text-foreground px-3 py-2.5 font-mono whitespace-nowrap">
                    {line.job_no || "—"}
                  </td>
                  <td className="text-foreground px-3 py-2.5">
                    {line.customer_name || line.customer_code || "—"}
                  </td>
                  <td className="text-muted-foreground px-3 py-2.5 whitespace-nowrap">
                    {line.status ? line.status.replace(/_/g, " ") : "—"}
                  </td>
                  {dateColumn && (
                    <td className="text-foreground px-3 py-2.5 whitespace-nowrap">
                      {formatLineDate(line[dateColumn.field])}
                    </td>
                  )}
                  <td className="text-muted-foreground px-3 py-2.5 whitespace-nowrap">
                    {line.due_day === 0
                      ? "Whole month"
                      : formatSnapshotDay(
                          landsOnIso(lines.month, line.due_day),
                        )}
                  </td>
                  <td
                    className={
                      "px-3 py-2.5 text-right font-mono whitespace-nowrap tabular-nums " +
                      (metric === "revenue"
                        ? "text-foreground"
                        : "text-muted-foreground")
                    }
                  >
                    {formatCurrencyGbp(line.revenue_amt)}
                  </td>
                  <td
                    className={
                      "px-3 py-2.5 text-right font-mono whitespace-nowrap tabular-nums " +
                      (metric === "contribution"
                        ? "text-foreground"
                        : "text-muted-foreground")
                    }
                  >
                    {formatCurrencyGbp(line.contribution_amt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lines.lines.length > 0 && (
        <div className="border-border text-muted-foreground border-t px-4 py-2.5 text-xs">
          {hasMoreThanShown ? (
            <>
              Showing the first {shown.length} of{" "}
              {formatCount(lines.lines.length)} rows — <b>Download CSV</b> for
              every one.{" "}
            </>
          ) : null}
          <b>Lands on</b> is the day the chart puts the money on, which is not
          always the job’s own date: work already overdue lands on today rather
          than the day it missed, so a late job shows both.
        </div>
      )}
    </div>
  );
}

/** A line's own date, or a dash — the field is null wherever the layer has no
 *  such date (a booked job has never been despatched). */
function formatLineDate(value: string | null | undefined): string {
  if (!value) return "—";
  // Timestamps (first_delivered_at) carry a time; the calendar day is what the
  // reader wants, and formatSnapshotDay reads ISO digits without a timezone
  // shift.
  return formatSnapshotDay(value.slice(0, 10));
}

/** The chart's X-axis day as an ISO date, so it formats like every other day
 *  label on the page. */
function landsOnIso(month: string, day: number): string {
  return `${month}-${String(day).padStart(2, "0")}`;
}
